import { useState, useEffect } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { SlidersHorizontal, Grid3X3, List, ChevronRight } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import ProductCard from "@/components/ProductCard";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ProductListing() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [showFilters, setShowFilters] = useState(false);

  const category = searchParams.get("category") || "";
  const subcategory = searchParams.get("subcategory") || "";
  const brand = searchParams.get("brand") || "";
  const search = searchParams.get("search") || "";
  const sort = searchParams.get("sort") || "relevance";
  const page = parseInt(searchParams.get("page") || "1");

  useEffect(() => {
    axios.get(`${API}/categories`).then(r => setCategories(r.data)).catch(() => {});
    axios.get(`${API}/brands`).then(r => setBrands(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (category) params.set("category", category);
    if (subcategory) params.set("subcategory", subcategory);
    if (brand) params.set("brand", brand);
    if (search) params.set("search", search);
    params.set("sort", sort);
    params.set("page", page);
    params.set("limit", 20);

    axios.get(`${API}/products?${params.toString()}`).then(r => {
      setProducts(r.data.products || []);
      setTotal(r.data.total || 0);
      setPages(r.data.pages || 1);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [category, subcategory, brand, search, sort, page]);

  const updateFilter = (key, value) => {
    const params = new URLSearchParams(searchParams);
    if (value) params.set(key, value); else params.delete(key);
    params.set("page", "1");
    setSearchParams(params);
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6" data-testid="product-listing-page">
      {/* Category Banner */}
      <div className="bg-gradient-to-r from-cyan-500 to-indigo-500 rounded-2xl p-6 mb-6 flex items-center justify-between overflow-hidden relative" data-testid="category-banner">
        <div className="relative z-10">
          <h1 className="text-xl sm:text-2xl font-bold text-white mb-1">{category || "All Products"}{subcategory ? ` - ${subcategory}` : ""}</h1>
          <p className="text-cyan-100 text-sm">Save up to 40% on genuine medicines & health products</p>
          {search && <p className="text-white/80 text-xs mt-1">Search results for: "<strong>{search}</strong>"</p>}
        </div>
        <div className="absolute right-0 top-0 bottom-0 w-1/3 bg-gradient-to-l from-white/10 to-transparent" />
        <div className="hidden sm:flex items-center gap-3 relative z-10">
          <div className="bg-white/15 backdrop-blur-sm rounded-xl px-4 py-2 text-white text-center border border-white/20">
            <p className="text-lg font-bold">{total}</p><p className="text-[10px] text-white/80">Products</p>
          </div>
        </div>
      </div>

      {/* Breadcrumb */}
      <nav className="flex items-center gap-2 text-sm text-slate-500 mb-6">
        <Link to="/" className="hover:text-cyan-600">Home</Link>
        <ChevronRight className="w-3 h-3" />
        <span className="text-slate-800 font-medium">{category || "All Products"}</span>
        {subcategory && <><ChevronRight className="w-3 h-3" /><span className="text-slate-800 font-medium">{subcategory}</span></>}
      </nav>

      <div className="flex gap-6">
        {/* Sidebar Filters */}
        <aside className={`${showFilters ? 'block fixed inset-0 z-40 bg-white p-6 overflow-auto' : 'hidden'} lg:block lg:relative lg:w-64 shrink-0`}>
          <div className="flex items-center justify-between lg:hidden mb-4">
            <h3 className="font-semibold">Filters</h3>
            <Button variant="ghost" size="sm" onClick={() => setShowFilters(false)}>Close</Button>
          </div>

          <div className="space-y-6">
            <div>
              <h4 className="text-sm font-semibold text-slate-800 mb-3">Categories</h4>
              <div className="space-y-2">
                {categories.filter(c => c.name !== "Brands").map(cat => (
                  <button key={cat.category_id} onClick={() => updateFilter("category", category === cat.name ? "" : cat.name)}
                    className={`block text-sm w-full text-left px-3 py-1.5 rounded-xl transition-colors ${category === cat.name ? 'bg-cyan-50 text-cyan-700 font-medium' : 'text-slate-600 hover:bg-slate-50'}`}>
                    {cat.name}
                  </button>
                ))}
              </div>
            </div>
            {category && categories.find(c => c.name === category)?.subcategories?.length > 0 && (
              <div>
                <h4 className="text-sm font-semibold text-slate-800 mb-3">Subcategories</h4>
                <div className="space-y-2">
                  {categories.find(c => c.name === category).subcategories.map(sub => (
                    <button key={sub} onClick={() => updateFilter("subcategory", subcategory === sub ? "" : sub)}
                      className={`block text-sm w-full text-left px-3 py-1.5 rounded-xl transition-colors ${subcategory === sub ? 'bg-cyan-50 text-cyan-700 font-medium' : 'text-slate-600 hover:bg-slate-50'}`}>
                      {sub}
                    </button>
                  ))}
                </div>
              </div>
            )}
            <div>
              <h4 className="text-sm font-semibold text-slate-800 mb-3">Brands</h4>
              <div className="space-y-2">
                {brands.map(b => (
                  <button key={b.brand_id} onClick={() => updateFilter("brand", brand === b.name ? "" : b.name)}
                    className={`block text-sm w-full text-left px-3 py-1.5 rounded-xl transition-colors ${brand === b.name ? 'bg-cyan-50 text-cyan-700 font-medium' : 'text-slate-600 hover:bg-slate-50'}`}>
                    {b.name}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </aside>

        {/* Product Grid */}
        <div className="flex-1">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <Button variant="outline" size="sm" className="lg:hidden rounded-xl" onClick={() => setShowFilters(true)} data-testid="filter-toggle-btn">
                <SlidersHorizontal className="w-4 h-4 mr-1" /> Filters
              </Button>
              <p className="text-sm text-slate-500">{total} products found</p>
            </div>
            <Select value={sort} onValueChange={(v) => updateFilter("sort", v)}>
              <SelectTrigger className="w-44 rounded-xl text-sm" data-testid="sort-select">
                <SelectValue placeholder="Sort by" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="relevance">Relevance</SelectItem>
                <SelectItem value="price_low">Price: Low to High</SelectItem>
                <SelectItem value="price_high">Price: High to Low</SelectItem>
                <SelectItem value="discount">Discount</SelectItem>
                <SelectItem value="rating">Rating</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {loading ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
              {[...Array(8)].map((_, i) => (
                <div key={i} className="bg-white rounded-2xl overflow-hidden">
                  <div className="aspect-square skeleton-loading" />
                  <div className="p-4 space-y-2">
                    <div className="h-3 skeleton-loading rounded w-16" />
                    <div className="h-4 skeleton-loading rounded w-full" />
                    <div className="h-6 skeleton-loading rounded w-20" />
                  </div>
                </div>
              ))}
            </div>
          ) : products.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-lg text-slate-500 mb-4">No products found</p>
              <Link to="/products"><Button className="rounded-full bg-cyan-600 hover:bg-cyan-700">View All Products</Button></Link>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="product-grid">
                {products.map(p => <ProductCard key={p.product_id} product={p} />)}
              </div>
              {pages > 1 && (
                <div className="flex justify-center gap-2 mt-8">
                  {Array.from({ length: pages }, (_, i) => i + 1).slice(0, 5).map(p => (
                    <button key={p} onClick={() => updateFilter("page", p.toString())}
                      className={`w-10 h-10 rounded-xl text-sm font-medium transition-colors ${p === page ? 'bg-cyan-600 text-white' : 'bg-white text-slate-600 hover:bg-cyan-50'}`}>
                      {p}
                    </button>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
